"use client";
import { useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { AtSign } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { capture } from "@/lib/posthog/client";
import { EVENTS } from "@/lib/posthog/events";
import { loginFailureReason } from "@/lib/posthog/login-events";
import { cn } from "@/lib/utils";
import { AuthTextField } from "@/components/molecules/auth/auth-text-field";
import { AuthPasswordField } from "@/components/molecules/auth/auth-password-field";
import { RememberMeToggle } from "@/components/molecules/auth/remember-me-toggle";
import { AuthSubmitButton } from "@/components/atoms/auth/auth-submit-button";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/dashboard";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Always clear any stale 2-FA cookie before starting a new login flow
    document.cookie = "twoFactorVerified=; path=/; max-age=0";

    try {
      const res = await authClient.signIn.email({
        email,
        password,
        rememberMe: remember,
      });
      setLoading(false);

      if (res.error) {
        // The shown message stays exactly as it was; only the
        // shape-guarded reason_code is captured, never res.error.message.
        capture(EVENTS.LOGIN_FAILED, loginFailureReason(res.error));
        setError(res.error.message ?? "An unexpected error occurred");
        return;
      }

      // Decision: credentials accepted counts as `login`, even on the
      // 2FA branch — staff accounts are forced into 2FA, so gating `login`
      // on the non-2FA branch would silently undercount every staff login.
      capture(EVENTS.LOGIN, {});

      if (res.data.twoFactorRedirect) {
        // The user is being asked for a TOTP code next.
        capture(EVENTS.TWO_FACTOR_PROMPTED, { method: "totp" });
        // Pass email so the 2FA page can show context; verification still goes through Better Auth.
        const q = new URLSearchParams({ email }).toString();
        router.push(`/2fa/verify?${q}`);
      } else {
        router.push(callbackUrl);
      }
    } catch (error) {
      setLoading(false);
      capture(EVENTS.LOGIN_FAILED, loginFailureReason(error));
      setError("An unexpected error occurred during login");
      console.error("Login error:", error);
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-8", className)} {...props}>
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-4xl font-bold tracking-tight text-brand-ink">
          Sign in to Niftipay
        </h1>
        <p className="text-base text-brand-ink/70">
          Login with your account to start accepting payments
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <AuthTextField
          id="email"
          label="Email address"
          type="email"
          autoComplete="email"
          required
          icon={<AtSign className="size-5" />}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          disabled={loading}
        />

        <div className="flex flex-col gap-3">
          <AuthPasswordField
            id="password"
            label="Password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
            disabled={loading}
          />
          <Link
            href="/forgot-password"
            className="text-sm font-semibold text-brand hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <div aria-live="polite">
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <RememberMeToggle checked={remember} onCheckedChange={setRemember} disabled={loading} />

        <AuthSubmitButton
          loading={loading}
          className="w-full sm:w-auto sm:self-start sm:px-12"
        >
          {loading ? "Signing in…" : "Sign In"}
        </AuthSubmitButton>
      </form>
    </div>
  );
}
